import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useSubscriptions,
  termInfo,
  endInfo,
  dunningInfo,
  CrmSubscription,
  SubscriptionPayment,
} from '../../contexts/SubscriptionsContext';
import { useAppData } from '../../providers/AppDataProvider';
import { Customer } from '../customers/CustomerInterfaces';
import NewArrangementModal from './NewArrangementModal';
import PaymentScheduleModal from './PaymentScheduleModal';
import { collection, doc, getDoc, getFirestore, onSnapshot, orderBy, query } from 'firebase/firestore';
import InvoiceViewModal from '../invoices/components/InvoiceViewModal';
import { InvoiceProvider } from '../invoices/contexts/InvoiceContext';
import { Invoice } from '../../types/accounting';
import { authFetch } from '../../utils/cloudFunctionsAuth';

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

// ---------------------------------------------------------------------------
// Recurring Payments — negotiated monthly support arrangements paid by Stripe
// subscription. Captured automatically by the Stripe webhook; the owner links
// each arrangement to a CRM customer, after which every paid cycle generates a
// native invoice that is already marked PAID.
// ---------------------------------------------------------------------------

const money = (n: number, currency = 'AUD') =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

const fmtDate = (ymd?: string | null): string => {
  if (!ymd) return '—';
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_BADGE: Record<string, string> = {
  active: 'badge-light-success',
  trialing: 'badge-light-info',
  past_due: 'badge-light-danger',
  unpaid: 'badge-light-danger',
  canceled: 'badge-light-secondary',
  incomplete: 'badge-light-warning',
  incomplete_expired: 'badge-light-secondary',
  paused: 'badge-light-warning',
};

const statusLabel = (s: string) => s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** Best-effort customer suggestion for an unlinked arrangement: exact email
 *  match first, then a unique name match. Deliberately conservative — a wrong
 *  preselect here would invoice the wrong customer. */
function suggestCustomerId(sub: CrmSubscription, customers: Customer[]): string {
  const email = (sub.customerEmail || '').trim().toLowerCase();
  if (email) {
    const byEmail = customers.filter((c) => (c.email || '').trim().toLowerCase() === email);
    if (byEmail.length === 1) return byEmail[0].id || '';
  }
  const name = (sub.customerName || '').trim().toLowerCase();
  if (name.length >= 4) {
    const byName = customers.filter((c) => {
      const co = (c.company || '').trim().toLowerCase();
      const nm = (c.name || '').trim().toLowerCase();
      return co === name || nm === name;
    });
    if (byName.length === 1) return byName[0].id || '';
  }
  return '';
}

// ---------------------------------------------------------------------------
// Review row — an arrangement awaiting a customer link
// ---------------------------------------------------------------------------

const ReviewRow: React.FC<{ sub: CrmSubscription; customers: Customer[] }> = ({ sub, customers }) => {
  const { linkSubscription, dismissSubscription } = useSubscriptions();
  const suggested = useMemo(() => suggestCustomerId(sub, customers), [sub, customers]);
  // NULL means "untouched, use the suggestion"; '' is a real choice the owner
  // made (they cleared it), which must disarm the Link button. `selected || …`
  // conflated the two, so a rejected suggestion could still be linked.
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<'link' | 'dismiss' | null>(null);
  const choice = selected ?? suggested;
  const term = termInfo(sub);

  const handleLink = async () => {
    if (!choice) { toast.warning('Pick the customer this arrangement belongs to.'); return; }
    setBusy('link');
    const res = await linkSubscription(sub.id, choice);
    setBusy(null);
    if (res.ok) {
      const n = res.invoicesCreated || 0;
      toast.success(
        `✅ Linked to ${res.customerName || 'customer'}${n > 0 ? ` — ${n} paid invoice${n === 1 ? '' : 's'} created` : ''}`,
        { autoClose: 8000 },
      );
    } else {
      toast.error(res.error || 'Could not link this arrangement');
    }
  };

  const handleDismiss = async () => {
    setBusy('dismiss');
    const res = await dismissSubscription(sub.id);
    setBusy(null);
    if (!res.ok) toast.error(res.error || 'Could not dismiss');
  };

  return (
    <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3 bg-body rounded border border-gray-300 p-4">
      <div className="flex-grow-1">
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="fw-bold text-dark fs-6">
            {money(sub.currentAmount, sub.currency)}<span className="text-muted fs-8 fw-semibold">/{sub.interval}</span>
          </span>
          {sub.testMode && <span className="badge badge-light-danger fs-8">TEST</span>}
          <span className={`badge ${STATUS_BADGE[sub.status] || 'badge-light-secondary'} fs-8`}>
            {statusLabel(sub.status)}
          </span>
        </div>
        <div className="text-muted fs-8">
          {[sub.customerName, sub.customerEmail].filter(Boolean).join(' · ') || 'Unknown payer'}
        </div>
        <div className="text-muted fs-8">
          {sub.planName && <span className="me-2">{sub.planName}</span>}
          <span className="badge badge-light-info fs-9">{term.label}</span>
        </div>
      </div>

      <div style={{ minWidth: 280 }}>
        {sub.testMode ? (
          <div className="text-muted fs-8 fst-italic">
            Test-mode subscription — no real money. Dismiss it once you've verified the flow.
          </div>
        ) : (
          <>
            <select
              className="form-select form-select-sm"
              value={choice}
              onChange={(e) => setSelected(e.target.value)}
              disabled={busy !== null}
            >
              <option value="">Select the customer…</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company || c.name}{c.email ? ` — ${c.email}` : ''}{c.id === suggested ? '  ★' : ''}
                </option>
              ))}
            </select>
            {suggested && choice === suggested && (
              <div className="text-success fs-9 mt-1">★ Suggested match</div>
            )}
            <div className="text-muted fs-9 mt-1">
              Linking creates a paid invoice for each payment already received.
            </div>
          </>
        )}
      </div>

      <div className="d-flex gap-2">
        <button
          className="btn btn-sm btn-primary text-nowrap"
          onClick={handleLink}
          disabled={busy !== null || !choice || sub.testMode}
        >
          {busy === 'link'
            ? <><span className="spinner-border spinner-border-sm me-1"></span>Linking…</>
            : <><i className="fas fa-link me-1"></i>Link</>}
        </button>
        <button
          className="btn btn-sm btn-light"
          onClick={handleDismiss}
          disabled={busy !== null}
          title="Not a customer arrangement"
        >
          {busy === 'dismiss' ? <span className="spinner-border spinner-border-sm"></span> : 'Dismiss'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Arrangement row (linked) — term progress + expandable payment history
// ---------------------------------------------------------------------------

const PaymentHistory: React.FC<{
  rows: SubscriptionPayment[];
  onOpenInvoice: (invoiceId: string) => void;
}> = ({ rows, onOpenInvoice }) => {
  const [emailing, setEmailing] = useState<string | null>(null);

  /** Draft a "payment received, paid invoice attached" email for review. Never
   *  sends — it lands in the drafts queue like every other invoice email. */
  const emailReceipt = async (p: SubscriptionPayment) => {
    if (!p.nativeInvoiceId) return;
    setEmailing(p.id);
    try {
      const res = await authFetch(`${CF_BASE}/draftInvoiceEmailHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId: p.nativeInvoiceId, template: 'payment-received' }),
      });
      const data = await res.json().catch(() => ({ ok: false, error: 'Unexpected response' }));
      if (data.ok) {
        toast.success(`Receipt drafted${data.to ? ` to ${data.to}` : ''} — review and send it from your Drafts.`);
      } else {
        toast.error(data.error || 'Could not draft the receipt');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Could not draft the receipt');
    } finally {
      setEmailing(null);
    }
  };

  if (rows.length === 0) {
    return <div className="text-muted fs-8 py-3">No payments received yet.</div>;
  }
  return (
    <div className="table-responsive">
      <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 mb-0">
        <thead>
          <tr className="fw-bold text-muted fs-8 text-uppercase">
            <th className="min-w-100px">Paid</th>
            <th className="min-w-150px">Period</th>
            <th className="min-w-100px text-end">Amount</th>
            <th className="min-w-120px">Invoice</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id}>
              <td className="text-muted fs-8">{fmtDate(p.paidAt)}</td>
              <td className="text-muted fs-8">
                {p.periodStart || p.periodEnd
                  ? `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}`
                  : '—'}
              </td>
              <td className="text-end fw-semibold fs-8">{money(p.amount, p.currency)}</td>
              <td className="fs-8">
                {p.nativeInvoiceNumber ? (
                  <div className="d-flex align-items-center gap-1 flex-wrap">
                    <button
                      type="button"
                      className="badge badge-light-success border-0"
                      style={{ cursor: 'pointer' }}
                      title="Open this invoice"
                      onClick={(e) => { e.stopPropagation(); onOpenInvoice(p.nativeInvoiceId!); }}
                    >
                      {p.nativeInvoiceNumber} · paid
                    </button>
                    {p.arrangementPhase === 2 && (
                      <span className="badge badge-light-info" title="Stepped-down rate">phase 2</span>
                    )}
                    {p.refundedAmount > 0 && (
                      <span
                        className="badge badge-light-danger"
                        title={`Refunded${p.refundedAt ? ` on ${fmtDate(p.refundedAt)}` : ''}`}
                      >
                        {p.fullyRefunded ? 'refunded' : `refunded ${money(p.refundedAmount, p.currency)}`}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-light-primary"
                      style={{ width: 24, height: 24 }}
                      title="Draft a &quot;payment received&quot; email with the paid invoice attached"
                      disabled={emailing === p.id}
                      onClick={(e) => { e.stopPropagation(); emailReceipt(p); }}
                    >
                      {emailing === p.id
                        ? <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} />
                        : <i className="bi bi-envelope" style={{ fontSize: 10 }} />}
                    </button>
                  </div>
                ) : p.testMode ? (
                  <span className="badge badge-light-danger">test</span>
                ) : p.invoiceError ? (
                  <span className="d-inline-flex flex-column">
                    <span className="badge badge-light-danger">
                      <i className="bi bi-exclamation-triangle me-1" />needs attention
                    </span>
                    {/* Show the reason — a tooltip alone hid why it failed. */}
                    <span className="text-danger fs-9 mt-1" style={{ maxWidth: 320 }}>{p.invoiceError}</span>
                  </span>
                ) : (
                  <span className="badge badge-light-warning">not invoiced</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ArrangementRow: React.FC<{
  sub: CrmSubscription;
  onOpenInvoice: (invoiceId: string) => void;
  onOpenSchedule: (subscriptionId: string) => void;
}> = ({ sub, onOpenInvoice, onOpenSchedule }) => {
  const { paymentsFor, unlinkSubscription, linkSubscription } = useSubscriptions();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rows = paymentsFor(sub.id);
  const term = termInfo(sub);
  const end = endInfo(sub);
  const dunn = dunningInfo(sub);
  // Net of refunds — gross would overstate what this arrangement actually earned.
  const received = rows.reduce((s, p) => s + (p.amount || 0) - (p.refundedAmount || 0), 0);

  const pct = term.fixedTerm && term.total
    ? Math.min(100, Math.round((term.paid / term.total) * 100))
    : null;

  const handleUnlink = async () => {
    setBusy(true);
    const res = await unlinkSubscription(sub.id);
    setBusy(false);
    if (res.ok) toast.info('Unlinked. Invoices already generated were kept.');
    else toast.error(res.error || 'Could not unlink');
  };

  return (
    <>
      <tr className="cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <td className="ps-5 py-4">
          <div className="fw-bold text-dark fs-7">{sub.linkedCustomerName || '—'}</div>
          <div className="text-muted fs-8">{sub.customerEmail}</div>
        </td>
        <td className="py-4">
          <div className="text-gray-800 fs-7">
            {sub.arrangementDescription || sub.planName || 'Monthly support'}
          </div>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {sub.testMode && <span className="badge badge-light-danger fs-9">TEST</span>}
            {sub.clientRef && <span className="badge badge-light-dark fs-9">{sub.clientRef}</span>}
            {sub.committedTerm && <span className="badge badge-light-primary fs-9">committed</span>}
            {sub.scheduleError && (
              <span className="badge badge-light-warning fs-9" title={sub.scheduleError}>
                no step-down
              </span>
            )}
          </div>
        </td>
        <td className="py-4">
          <span className="fw-bold text-dark fs-7">{money(sub.currentAmount, sub.currency)}</span>
          <span className="text-muted fs-9">/{sub.interval}</span>
        </td>
        <td className="py-4" style={{ minWidth: 190 }}>
          <div className="text-gray-800 fs-8">{term.label}</div>
          {pct !== null && (
            <div className="progress mt-1" style={{ height: 4 }}>
              <div className="progress-bar bg-primary" style={{ width: `${pct}%` }} />
            </div>
          )}
        </td>
        <td className="py-4">
          <span className={`badge ${STATUS_BADGE[sub.status] || 'badge-light-secondary'} fs-8`}>
            {statusLabel(sub.status)}
          </span>
          {/* An end date alone is ambiguous — say WHICH kind of ending it is. */}
          {end.label && (
            <div className={`text-${end.tone} fs-9 mt-1`}>
              {end.reason === 'cancelled' && <i className="bi bi-person-dash me-1" />}
              {end.reason === 'term' && <i className="bi bi-check2-circle me-1" />}
              {end.label}
            </div>
          )}
        </td>
        <td className="py-4 text-muted fs-8">
          {end.ended || end.reason === 'cancelled' ? <span className="text-muted">—</span> : fmtDate(sub.currentPeriodEnd)}
        </td>
        <td className="py-4 text-end fw-semibold fs-8">{money(received)}</td>
        <td className="pe-4 py-4 text-end">
          <button type="button" className="btn btn-sm btn-light-primary me-3" aria-label={`Payment schedule for ${sub.linkedCustomerName || sub.customerName || 'customer'}`} title="View and share a payment schedule" onClick={e => { e.stopPropagation(); onOpenSchedule(sub.id); }}>
            <i className="bi bi-calendar3 me-1" aria-hidden="true" /> Schedule
          </button>
          <i className={`bi bi-chevron-${open ? 'up' : 'down'} text-muted`}></i>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} className="bg-light px-5 py-4">
            {/* The negotiated deal — what the customer actually agreed to. */}
            {(sub.arrangementLineItems?.length || sub.clientRef || sub.committedTerm || sub.scheduleError) && (
              <div className="mb-4 p-3 rounded bg-body border border-gray-300">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <span className="fw-bold text-gray-800 fs-7">
                    {sub.arrangementDescription || 'Agreed deal'}
                  </span>
                  {sub.clientRef && (
                    <span className="badge badge-light-dark fs-9">Ref: {sub.clientRef}</span>
                  )}
                  {sub.committedTerm && (
                    <span
                      className="badge badge-light-primary fs-9"
                      title="Committed minimum term — the client may cancel, but up-front work may be withdrawn"
                    >
                      Committed term
                    </span>
                  )}
                  <span className="badge badge-light-secondary fs-9">
                    GST {sub.gstTreatment}
                  </span>
                </div>

                {sub.scheduleError && (
                  <div className="alert alert-warning py-2 px-3 fs-8 mb-2">
                    <i className="bi bi-exclamation-triangle me-2" />
                    Step-down schedule didn't attach — billing stays on the opening rate. ({sub.scheduleError})
                  </div>
                )}

                {sub.arrangementLineItems && sub.arrangementLineItems.length > 0 && (
                  <table className="table table-sm mb-0 fs-8">
                    <tbody>
                      {sub.arrangementLineItems.map((li, i) => (
                        <tr key={i}>
                          <td className="ps-0">{li.description || 'Item'}</td>
                          <td className="text-end" style={{ width: 60 }}>×{li.qty ?? 1}</td>
                          <td className="text-end" style={{ width: 100 }}>
                            {money(((li.unitCents ?? 0) * (li.qty ?? 1)) / 100)}
                          </td>
                        </tr>
                      ))}
                      <tr className="fw-bold border-top">
                        <td className="ps-0">Per {sub.interval}</td>
                        <td></td>
                        <td className="text-end">{money(sub.currentAmount, sub.currency)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
                {sub.phase2AmountIncGst != null && (
                  <div className="text-muted fs-8 mt-2">
                    Steps down to <span className="fw-semibold">{money(sub.phase2AmountIncGst)}</span>/{sub.interval} after the agreed term.
                  </div>
                )}
              </div>
            )}

            {/* A declined card is the thing most worth seeing here. */}
            {dunn && (
              <div className={`alert alert-${dunn.tone} py-2 px-3 fs-8 mb-3 d-flex align-items-center gap-2`}>
                <i className="bi bi-credit-card-2-front" />
                <span>{dunn.label}</span>
                {sub.dunning?.hostedInvoiceUrl && (
                  <a
                    className="btn btn-sm btn-light-primary ms-auto py-1"
                    href={sub.dunning.hostedInvoiceUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Open in Stripe
                  </a>
                )}
              </div>
            )}

            {/* Payments collected while this was still unlinked never got an
                invoice. Re-running the link backfills them through the same
                guarded path (test-mode, currency, totals-match). */}
            {(() => {
              // Include invoiceError rows: they were previously EXCLUDED, which
              // made a failed invoicing attempt a dead end with no way to retry.
              const stranded = rows.filter((p) => !p.nativeInvoiceNumber && !p.testMode);
              if (stranded.length === 0 || !sub.linkedCustomerId) return null;
              return (
                <div className="alert alert-warning d-flex align-items-center justify-content-between py-2 px-3 mb-3">
                  <span className="fs-8">
                    <i className="bi bi-exclamation-triangle me-2" />
                    {stranded.length} payment{stranded.length === 1 ? '' : 's'} received before this was linked
                    {' '}— not yet invoiced.
                  </span>
                  <button
                    className="btn btn-sm btn-warning"
                    disabled={busy}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setBusy(true);
                      const r = await linkSubscription(sub.id, sub.linkedCustomerId!);
                      setBusy(false);
                      if (r.ok) {
                        toast.success(`Invoiced ${r.invoicesCreated || 0} payment(s)`);
                      } else {
                        toast.error(r.error || 'Could not invoice');
                      }
                    }}
                  >
                    {busy ? <span className="spinner-border spinner-border-sm" /> : 'Invoice now'}
                  </button>
                </div>
              );
            })()}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="fw-bold text-gray-800 fs-7">
                Payment history ({rows.length})
              </span>
              <button
                className="btn btn-sm btn-light-danger"
                onClick={(e) => { e.stopPropagation(); handleUnlink(); }}
                disabled={busy}
                title="Detach from this customer (keeps invoices already generated)"
              >
                {busy ? <span className="spinner-border spinner-border-sm"></span> : 'Unlink'}
              </button>
            </div>
            <PaymentHistory rows={rows} onOpenInvoice={onOpenInvoice} />
          </td>
        </tr>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const RecurringPaymentsPage: React.FC = () => {
  const { subscriptions, payments, pending, loading, error } = useSubscriptions();
  const { customers } = useAppData();
  const [showNew, setShowNew] = useState(false);
  const [scheduleId, setScheduleId] = useState<string | null>(null);

  // Open the generated invoice from its badge in the payment history.
  const [openedInvoice, setOpenedInvoice] = useState<Invoice | null>(null);
  const openInvoice = async (invoiceId: string) => {
    try {
      const snap = await getDoc(doc(getFirestore(), 'invoices', invoiceId));
      if (!snap.exists()) { toast.error('That invoice no longer exists.'); return; }
      setOpenedInvoice({ id: snap.id, ...(snap.data() as any) } as Invoice);
    } catch (e: any) {
      toast.error(e?.message || 'Could not open the invoice');
    }
  };

  // Deals created here that nobody has subscribed to yet — the link is live and
  // waiting, so keep it one click from being re-sent.
  const [openDeals, setOpenDeals] = useState<any[]>([]);
  useEffect(() => {
    try {
      const db = getFirestore();
      return onSnapshot(
        query(collection(db, 'crm_arrangements'), orderBy('createdAt', 'desc')),
        (snap) => setOpenDeals(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
        (err) => console.warn('[Recurring] arrangements listener:', err),
      );
    } catch { return undefined; }
  }, []);
  const subscribedIds = useMemo(
    () => new Set(subscriptions.map((s) => s.arrangementId).filter(Boolean)),
    [subscriptions],
  );
  // Only genuinely-open deals. The website owns the lifecycle, so a cancelled or
  // already-subscribed arrangement must drop out — previously the mirror's
  // status was written once and never read, so those sat here forever.
  const awaitingSignup = openDeals.filter(
    (d) => !subscribedIds.has(d.id) && String(d.status || 'open') === 'open',
  );

  const [syncing, setSyncing] = useState(false);
  const syncArrangements = async () => {
    setSyncing(true);
    try {
      const res = await authFetch(`${CF_BASE}/syncArrangementsHttp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const data = await res.json().catch(() => ({ ok: false, error: 'Unexpected response' }));
      if (!data.ok) toast.error(data.error || 'Could not refresh');
      else if (data.updated > 0) toast.success(`Refreshed — ${data.updated} arrangement(s) updated`);
      else toast.info('All arrangements are already up to date');
    } catch (e: any) {
      toast.error(e?.message || 'Could not refresh');
    } finally {
      setSyncing(false);
    }
  };

  const sortedCustomers = useMemo(
    () => [...customers].sort((a, b) =>
      (a.company || a.name || '').localeCompare(b.company || b.name || '')),
    [customers],
  );

  const linked = useMemo(
    () => subscriptions
      .filter((s) => s.linkStatus === 'linked')
      .sort((a, b) => {
        // A failing card FIRST — it previously sank to the bottom, which is the
        // exact opposite of what an alert needs. Then live, then by value.
        const rank = (s: CrmSubscription) => (dunningInfo(s) ? 0 : s.status === 'active' ? 1 : 2);
        const ra = rank(a); const rb = rank(b);
        if (ra !== rb) return ra - rb;
        return (b.currentAmount || 0) - (a.currentAmount || 0);
      }),
    [subscriptions],
  );

  // Headline figures. MRR = what will actually bill again, so a subscription the
  // customer has cancelled (still "active" until its paid period runs out) is
  // excluded — counting it would overstate forward revenue.
  const billingOn = linked.filter((s) => {
    if (s.status !== 'active' || s.testMode) return false;
    return endInfo(s).reason !== 'cancelled';
  });
  // Normalise to a MONTHLY figure — a yearly arrangement was being added at its
  // full annual price, massively overstating monthly recurring revenue.
  const monthlyEquivalent = (s: CrmSubscription): number => {
    const amt = s.currentAmount || 0;
    const n = s.intervalCount || 1;
    switch ((s.interval || 'month').toLowerCase()) {
      case 'year': return amt / (12 * n);
      case 'week': return (amt * 52) / (12 * n);
      case 'day': return (amt * 365) / (12 * n);
      default: return amt / n;
    }
  };
  const mrr = billingOn.reduce((sum, s) => sum + monthlyEquivalent(s), 0);
  // Net of refunds — a refunded cycle is not money the business kept.
  const collected = payments
    .filter((p) => !p.testMode)
    .reduce((sum, p) => sum + (p.amount || 0) - (p.refundedAmount || 0), 0);
  const invoicedCount = payments.filter((p) => p.nativeInvoiceNumber).length;

  return (
    <div className="container-xxl py-6 px-6">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="fw-bolder text-dark mb-1">Recurring Payments</h1>
          <div className="text-muted fs-6">
            Ongoing support arrangements billed by Stripe — each payment is invoiced automatically
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          {/* A denied read must never look like a healthy empty feed. */}
          <span className={`badge ${error ? 'badge-light-danger' : loading ? 'badge-light-warning' : 'badge-light-success'} fs-7 fw-bold`}>
            <span
              className="bullet bullet-dot me-2"
              style={{ background: error ? 'var(--bs-danger)' : loading ? 'var(--bs-warning)' : 'var(--bs-success)' }}
            ></span>
            {error ? "Can't read" : loading ? 'Connecting…' : 'Live'}
          </span>
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>
            <i className="bi bi-plus-lg me-2" />New arrangement
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-5">
          <i className="bi bi-exclamation-octagon" />
          <span>Couldn't load arrangements — {error}. Figures below are incomplete.</span>
        </div>
      )}

      {/* KPI strip */}
      <div className="row g-4 mb-5">
        <div className="col-sm-4">
          <div className="card h-100">
            <div className="card-body py-4">
              <div className="text-muted fs-8 text-uppercase fw-bold mb-1">Monthly recurring</div>
              <div className="fs-2 fw-bolder text-success">{money(mrr)}</div>
              <div className="text-muted fs-9">
                {billingOn.length} still billing
                {(() => {
                  const leaving = linked.filter((s) => !s.testMode && endInfo(s).reason === 'cancelled' && s.status === 'active').length;
                  return leaving > 0 ? ` · ${leaving} cancelling` : '';
                })()}
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="card h-100">
            <div className="card-body py-4">
              <div className="text-muted fs-8 text-uppercase fw-bold mb-1">Collected to date</div>
              <div className="fs-2 fw-bolder text-dark">{money(collected)}</div>
              <div className="text-muted fs-9">{payments.filter((p) => !p.testMode).length} payment(s) received</div>
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="card h-100">
            <div className="card-body py-4">
              <div className="text-muted fs-8 text-uppercase fw-bold mb-1">Auto-invoiced</div>
              <div className="fs-2 fw-bolder text-dark">{invoicedCount}</div>
              <div className="text-muted fs-9">paid invoices generated</div>
            </div>
          </div>
        </div>
      </div>

      {/* Deals created here, waiting on the client to subscribe */}
      {awaitingSignup.length > 0 && (
        <div className="card mb-5">
          <div className="card-header border-0 pt-4 pb-0 d-flex align-items-center">
            <div>
              <div className="fw-bolder text-dark fs-5">Awaiting signup</div>
              <div className="text-muted fs-8">
                Links are live — the customer is already bound, so these link themselves on payment.
              </div>
            </div>
            <div className="ms-auto d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-light-primary"
                onClick={syncArrangements}
                disabled={syncing}
                title="Re-read each deal's status from the website — clears any that were cancelled or already signed"
              >
                {syncing
                  ? <><span className="spinner-border spinner-border-sm me-1" />Refreshing…</>
                  : <><i className="bi bi-arrow-clockwise me-1" />Refresh</>}
              </button>
              <span className="badge badge-light-primary fs-7">{awaitingSignup.length}</span>
            </div>
          </div>
          <div className="card-body pt-4">
            <div className="d-flex flex-column gap-2">
              {awaitingSignup.map((d) => (
                <div key={d.id} className="d-flex flex-wrap align-items-center gap-3 border border-gray-300 rounded p-3">
                  <div className="flex-grow-1">
                    <div className="fw-bold text-dark fs-7">{d.customerName}</div>
                    <div className="text-muted fs-8">
                      {d.description}
                      {d.termCycles ? ` · ${d.termCycles} ${d.billingInterval === 'year' ? 'years' : 'months'}` : ' · ongoing'}
                      {d.clientRef ? ` · ${d.clientRef}` : ''}
                    </div>
                  </div>
                  {!d.customerId && (
                    <span className="badge badge-light-warning fs-9" title="No CRM contact bound — you'll link it manually">
                      no contact
                    </span>
                  )}
                  {/* An unsigned link has no natural expiry, so show its age —
                      otherwise a long-abandoned deal looks identical to one
                      sent this morning. */}
                  {(() => {
                    const created = d.createdAt?.seconds ? d.createdAt.seconds * 1000 : null;
                    if (!created) return null;
                    const days = Math.floor((Date.now() - created) / 86400000);
                    if (days < 7) return <span className="text-muted fs-9">sent {days === 0 ? 'today' : `${days}d ago`}</span>;
                    return (
                      <span className={`badge fs-9 ${days > 30 ? 'badge-light-danger' : 'badge-light-warning'}`}
                        title="This link has been out for a while with no signup">
                        {days}d unsigned
                      </span>
                    );
                  })()}
                  {d.url && (
                    <button
                      className="btn btn-sm btn-light-primary text-nowrap"
                      onClick={async () => {
                        try { await navigator.clipboard.writeText(d.url); toast.success('Link copied'); }
                        catch { toast.error('Could not copy — select the link and copy it manually'); }
                      }}
                    >
                      <i className="fas fa-copy me-1"></i>Copy link
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Review strip — arrangements awaiting a customer link */}
      {pending.length > 0 && (
        <div className="card mb-5 border border-primary border-dashed">
          <div className="card-header border-0 pt-4 pb-0 d-flex align-items-center">
            <div className="symbol symbol-40px me-3">
              <div className="symbol-label bg-light-primary">
                <i className="fas fa-link text-primary fs-4"></i>
              </div>
            </div>
            <div>
              <div className="fw-bolder text-dark fs-5">New arrangements — needs a customer</div>
              <div className="text-muted fs-8">
                Link each one to a contact. Payments already received are invoiced on link, and every
                future payment is invoiced automatically.
              </div>
            </div>
            <span className="badge badge-primary ms-auto fs-7">{pending.length}</span>
          </div>
          <div className="card-body pt-4">
            <div className="d-flex flex-column gap-3">
              {pending.map((s) => (
                <ReviewRow key={s.id} sub={s} customers={sortedCustomers} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Active arrangements */}
      <div className="card">
        <div className="card-header border-0 pt-5 px-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Arrangements</span>
            <span className="text-muted mt-1 fw-semibold fs-7">
              {linked.length} linked · click a row for payment history
            </span>
          </h3>
        </div>
        <div className="card-body p-0">
          {loading && (
            <div className="p-10 text-center text-muted">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Loading arrangements…
            </div>
          )}

          {!loading && linked.length === 0 && (
            <div className="py-12 text-center text-muted">
              <i className="fas fa-rotate fs-2x mb-4 text-gray-400 d-block"></i>
              {subscriptions.length === 0
                ? 'No recurring arrangements yet. They appear here automatically once a customer subscribes.'
                : 'No arrangements linked to a customer yet.'}
            </div>
          )}

          {!loading && linked.length > 0 && (
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 mb-0">
                <thead>
                  <tr className="fw-bolder text-muted bg-light fs-8 text-uppercase">
                    <th className="ps-5 w-200px">Customer</th>
                    <th className="w-150px">Plan</th>
                    <th className="w-110px">Amount</th>
                    <th className="w-200px">Term</th>
                    <th className="w-110px">Status</th>
                    <th className="w-120px">Next charge</th>
                    <th className="w-110px text-end">Received</th>
                    <th className="w-50px pe-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {linked.map((s) => (
                    <ArrangementRow key={s.id} sub={s} onOpenInvoice={openInvoice} onOpenSchedule={setScheduleId} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && linked.length > 0 && (
          <div className="card-footer py-3 px-5 text-muted fs-8 text-center border-top">
            Every payment creates a PAID invoice dated to the day Stripe collected it
          </div>
        )}
      </div>

      <NewArrangementModal show={showNew} onClose={() => setShowNew(false)} onCreated={() => {}} />
      {scheduleId && <PaymentScheduleModal key={scheduleId} subscriptionId={scheduleId} onClose={() => setScheduleId(null)} onOpenInvoice={openInvoice} />}

      {openedInvoice && (
        <InvoiceProvider>
          <InvoiceViewModal
            show={!!openedInvoice}
            invoice={openedInvoice}
            onHide={() => setOpenedInvoice(null)}
          />
        </InvoiceProvider>
      )}
    </div>
  );
};

export default RecurringPaymentsPage;
